@if (rows().length === 0) {
  <div class="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-200 py-14 text-center text-neutral-400">
    <mat-icon class="text-4xl!">table_rows</mat-icon>
    <p class="text-base">{{ "guaranteeV2.message.prescriptionsEmpty" | translate }}</p>
  </div>
} @else {
  <!--
    One track list stated here and inherited by every row through `grid-cols-subgrid`, so a column is
    as wide as its widest cell across the whole table rather than per row. The two text columns are
    given fractions rather than `auto`: a free-form prescription carries its own wording as its code,
    and on `auto` one such row would set the column width for the whole table.

    No row gap — the rail has to read as one unbroken line down a record's services.

    Below the breakpoint each row falls back to a wrapping line: seven columns in a narrow panel
    would scroll sideways, and every cell already carries its own icon and tooltip to stay legible
    without a header.
  -->
  <div class="grid gap-x-3 @3xl/table:grid-cols-[3px_auto_minmax(0,2fr)_minmax(0,1fr)_auto_auto_auto]">
    <!--
      Opaque: rows scroll under this, and a translucent header shows their borders through it.

      Every column keeps a header cell, the rail's included. A cell taken out of flow — `sr-only`
      is `position: absolute` — would shift every header after it one column left.
    -->
    <div class="sticky top-0 z-10 hidden bg-neutral-0 py-1.5 text-sm font-semibold text-neutral-400 @3xl/table:col-span-full @3xl/table:grid @3xl/table:grid-cols-subgrid">
      <span></span>
      <span>{{ "guaranteeV2.label.createdAt" | translate }}</span>
      <span>{{ "guaranteeV2.label.serviceName" | translate }}</span>
      <span>{{ "guaranteeV2.label.serviceCode" | translate }}</span>
      <span>{{ "guaranteeV2.label.course" | translate }}</span>
      <span>{{ "guaranteeV2.label.decisionFacet" | translate }}</span>
      <span>{{ "guaranteeV2.label.fulfillmentFacet" | translate }}</span>
    </div>

    @for (band of bands(); track band.row.key) {
      <button
        class="flex w-full flex-wrap items-center gap-x-2 gap-y-1 border border-transparent px-2 py-1 text-left hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none @3xl/table:col-span-full @3xl/table:grid @3xl/table:grid-cols-subgrid @3xl/table:items-center @3xl/table:border-x-0 @3xl/table:border-b-0 @3xl/table:px-0"
        type="button"
        [class]="band.startsRecord ? 'border-t-neutral-200' : 'border-t-transparent'"
        (click)="guaranteeOpened.emit(band.row.guaranteeId)"
      >
        <!--
          Stretched rather than centred: a rail as tall as its text would read as a bullet, not a
          bracket. Stretching alone stops at the row's content box, so the row padding shows through
          as a gap and a record reads as a column of ticks — the negative margin bridges it, and the
          rails of one record meet as a single line.
        -->
        <span
          class="w-0.5 shrink-0 self-stretch rounded-full @3xl/table:-my-1 @3xl/table:w-full @3xl/table:rounded-none"
          [class]="band.rail"
        ></span>

        <!--
          Drawn once per record. Repeating it down a course is noise the rail already carries — but
          the rail is colour, which a screen reader cannot read, so the date stays in the tree on
          every row and only stops being painted. `sr-only` is safe here where it was not on the
          header: it is inside the cell rather than being the cell, so no grid item leaves the flow.
        -->
        <span class="shrink-0 text-sm text-neutral-500 tabular-nums">
          @if (band.startsRecord) {
            {{ band.row.createdAt | date: "d MMM yyyy" }}
          } @else {
            <span class="sr-only">{{ band.row.createdAt | date: "d MMM yyyy" }}</span>
          }
        </span>

        <span class="flex min-w-0 flex-1 items-center gap-1.5 @3xl/table:flex-none">
          <mat-icon
            class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
            [matTooltip]="kindLabelKeys[band.row.line.prescription.kind] | translate"
            >{{ kindIcons[band.row.line.prescription.kind] }}</mat-icon
          >
          <!--
            The kind is drawn as an icon and named only by a tooltip, which a pointer has to find and
            a screen reader announces as the icon's ligature. Stated here so the row says what kind
            it is on its own. Safe inside this cell, unlike in the header: it is not a grid item, so
            taking it out of flow shifts no column.
          -->
          <span
            class="sr-only"
            data-testid="prescription-kind"
            >{{ kindLabelKeys[band.row.line.prescription.kind] | translate }}</span
          >
          <span
            class="min-w-0 truncate text-sm font-semibold text-neutral-800"
            [class.text-neutral-400]="band.row.line.prescription.isDeleted"
            [class.line-through]="band.row.line.prescription.isDeleted"
            [matTooltip]="band.row.line.prescription.name"
            >{{ band.row.line.prescription.name }}</span
          >
          <!-- Only ever reachable with «показати видалені» on, so the row says why it is here at all. -->
          @if (band.row.line.prescription.isDeleted) {
            <guarantees-chip
              class="shrink-0"
              variant="outline"
              icon="delete"
              [tooltip]="'guaranteeV2.message.deletedRecordHint' | translate"
              >{{ "guaranteeV2.label.deleted" | translate }}</guarantees-chip
            >
          }
        </span>

        <!-- Truncated only once it is a column: wrapped, it sizes to its text and has nothing to clip against. -->
        <span
          class="shrink-0 text-sm text-neutral-400 tabular-nums @3xl/table:min-w-0 @3xl/table:truncate"
          data-testid="prescription-code"
          [matTooltip]="band.row.line.prescription.code"
          >{{ band.row.line.prescription.code }}</span
        >

        <!-- A course keeps a cell even when empty, so the column stays a column rather than a ragged edge. -->
        <span class="shrink-0 text-sm text-neutral-500 tabular-nums">
          @if (band.row.courseSize) {
            <span [matTooltip]="'guaranteeV2.message.prescriptionSetHint' | translate: { count: band.row.courseSize }"
              >{{ band.row.coursePosition }}/{{ band.row.courseSize }}</span
            >
          }
        </span>

        <!-- The robot in place of the tick where the engine granted cover, as it is down the stream. -->
        <guarantees-chip
          class="shrink-0"
          data-testid="prescription-decision"
          [tone]="decisionTones[band.row.decision]"
          [icon]="band.auto ? autoIcon : decisionIcons[band.row.decision]"
          [tooltip]="(band.auto ? autoHintKey : decisionHintKeys[band.row.decision]) | translate"
          >{{ decisionLabelKeys[band.row.decision] | translate }}</guarantees-chip
        >

        <guarantees-chip
          class="shrink-0"
          variant="outline"
          data-testid="prescription-fulfillment"
          [tone]="fulfillmentTones[band.row.line.fulfillment]"
          [icon]="fulfillmentIcons[band.row.line.fulfillment]"
          >{{ fulfillmentLabelKeys[band.row.line.fulfillment] | translate }}</guarantees-chip
        >
      </button>
    }
  </div>
}
